// -----------------------------------------------------------------------------
// ui/timeline/interactions.ts — pointer handling for the timeline canvas.
//
// Translates clicks/drags on the canvas into edits: select a clip, move it (and
// across tracks), trim either edge (with snapping to the frame grid and clamping
// to the source's available range), scrub the playhead on the ruler, and remove a
// track via its ✕. Live feedback is drawn through the view's transient `drag`
// state; the actual undoable Command is dispatched once on pointer-up.
// -----------------------------------------------------------------------------
import { MoveClipCommand, TrimClipCommand } from '../../core/commands/clip-commands';
import { RemoveTrackCommand } from '../../core/commands/track-commands';
import { type MediaClip, clipEnd, locateClip } from '../../core/model';
import { snapToFrame } from '../../shared/time';
import type { EditorContext } from '../context';
import { type TimelineView } from './timeline';

interface DragOrigin {
  mode: 'move' | 'trim-start' | 'trim-end';
  startSec: number;
  inSec: number;
  endSec: number;
  trackIndex: number;
  grabOffsetSec: number; // pointer time − clip start, for moves
  assetDuration: number; // source length, 0 = unbounded (still image / text)
  isMedia: boolean;
}

export function attachTimelineInteractions(view: TimelineView, ctx: EditorContext): void {
  const canvas = view.canvas;
  let origin: DragOrigin | null = null;
  let scrubbing = false;

  const minDur = () => 1 / ctx.store.state.project.fps;

  function localPos(e: PointerEvent): { x: number; y: number } {
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  canvas.addEventListener('pointerdown', (e) => {
    const { x, y } = localPos(e);
    const hit = view.hitTest(x, y);

    if (hit.kind === 'ruler') {
      scrubbing = true;
      ctx.player.seek(view.xToTime(x));
      canvas.setPointerCapture(e.pointerId);
      return;
    }
    if (hit.kind === 'remove-track') {
      // Instantaneous action — no pointer capture needed (it's not a drag). The
      // command itself clears any selection that lived on the removed track.
      const track = ctx.store.state.project.tracks[hit.trackIndex];
      if (track) {
        ctx.store.dispatch(new RemoveTrackCommand(track.id));
        ctx.setStatus(`Removed track “${track.name}”.`);
      }
      return;
    }
    if (hit.kind === 'empty' || hit.kind === 'outside') {
      ctx.store.select(null);
      return;
    }

    // hit.kind === 'clip'
    ctx.store.select(hit.clipId);
    const loc = locateClip(ctx.store.state.project, hit.clipId);
    if (!loc) return;
    const clip = loc.clip;
    const isMedia = clip.kind !== 'text';
    const asset = isMedia ? ctx.store.getAsset((clip as MediaClip).assetId) : undefined;

    origin = {
      mode: hit.edge === 'start' ? 'trim-start' : hit.edge === 'end' ? 'trim-end' : 'move',
      startSec: clip.startSec,
      inSec: isMedia ? (clip as MediaClip).inSec : 0,
      endSec: clipEnd(clip),
      trackIndex: hit.trackIndex,
      grabOffsetSec: view.xToTime(x) - clip.startSec,
      assetDuration: asset?.durationSec ?? 0,
      isMedia,
    };
    view.drag = {
      clipId: hit.clipId,
      mode: origin.mode,
      startSec: clip.startSec,
      durationSec: clip.durationSec,
      inSec: origin.inSec,
      trackIndex: hit.trackIndex,
    };
    canvas.setPointerCapture(e.pointerId);
    view.draw();
  });

  canvas.addEventListener('pointermove', (e) => {
    const { x, y } = localPos(e);

    if (scrubbing) {
      ctx.player.seek(view.xToTime(x));
      return;
    }
    if (!origin || !view.drag) return;

    const fps = ctx.store.state.project.fps;
    const pointerTime = snapToFrame(view.xToTime(x), fps);
    const min = minDur();

    if (origin.mode === 'move') {
      const trackCount = ctx.store.state.project.tracks.length;
      const newStart = Math.max(0, snapToFrame(pointerTime - origin.grabOffsetSec, fps));
      const newTrack = Math.min(Math.max(0, view.trackIndexAtY(y)), trackCount - 1);
      view.drag.startSec = newStart;
      view.drag.trackIndex = newTrack;
    } else if (origin.mode === 'trim-start') {
      const lower = origin.isMedia ? Math.max(0, origin.startSec - origin.inSec) : 0;
      const upper = origin.endSec - min;
      const newStart = Math.min(Math.max(pointerTime, lower), upper);
      view.drag.startSec = newStart;
      view.drag.durationSec = origin.endSec - newStart;
      view.drag.inSec = origin.isMedia ? origin.inSec + (newStart - origin.startSec) : 0;
    } else {
      // trim-end
      const lower = origin.startSec + min;
      const upper =
        origin.isMedia && origin.assetDuration > 0
          ? origin.startSec + (origin.assetDuration - origin.inSec)
          : Number.POSITIVE_INFINITY;
      const newEnd = Math.min(Math.max(pointerTime, lower), upper);
      view.drag.durationSec = newEnd - origin.startSec;
    }
    view.draw();
  });

  function endDrag(e: PointerEvent): void {
    if (scrubbing) {
      scrubbing = false;
      if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
      return;
    }
    if (!origin || !view.drag) return;
    const drag = view.drag;

    if (origin.mode === 'move') {
      const track = ctx.store.state.project.tracks[drag.trackIndex];
      if (track) ctx.store.dispatch(new MoveClipCommand(drag.clipId, track.id, drag.startSec));
    } else {
      ctx.store.dispatch(
        new TrimClipCommand(drag.clipId, {
          startSec: drag.startSec,
          inSec: drag.inSec,
          durationSec: drag.durationSec,
        }),
      );
    }

    origin = null;
    view.drag = null;
    if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
    view.draw();
  }

  canvas.addEventListener('pointerup', endDrag);
  canvas.addEventListener('pointercancel', endDrag);

  // Cursor affordance: resize arrows on edges, grab on bodies.
  canvas.addEventListener('pointermove', (e) => {
    if (origin || scrubbing) return;
    const { x, y } = localPos(e);
    const hit = view.hitTest(x, y);
    canvas.style.cursor =
      hit.kind === 'clip'
        ? hit.edge === 'body'
          ? 'grab'
          : 'ew-resize'
        : hit.kind === 'ruler'
          ? 'pointer'
          : hit.kind === 'remove-track'
            ? 'pointer'
            : 'default';
  });
}
